import { ActionContext } from "vuex";
import { RootState } from "../../store";
import { PlanTrailState } from "../PlanTrailModule";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { snapPoints } from "../../../../../utils/models/Graphooper";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { debounce } from "../../../../../utils/helpers/debounce";
import { MoveMarkerCommand } from "../../undo/commands/MoveMarkerCommand";

type ActionContextType = ActionContext<PlanTrailState, RootState>;

// Debounce delay for API calls during drag
const DRAG_PREVIEW_DEBOUNCE_MS = 500;

// Create preview layers on the map
function createPreviewLayers() {
  console.log('[DRAG_PREVIEW] createPreviewLayers called');
  if (!window.map) {
    console.log('[DRAG_PREVIEW] window.map not available');
    return;
  }

  // Add source for preview lines
  const sourceExists = window.map.getSource('drag-preview-lines');
  console.log('[DRAG_PREVIEW] drag-preview-lines source exists:', !!sourceExists);

  if (!sourceExists) {
    console.log('[DRAG_PREVIEW] Creating drag-preview-lines source');
    window.map.addSource('drag-preview-lines', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
    console.log('[DRAG_PREVIEW] drag-preview-lines source created');
  }

  // Find a good layer to insert before (above existing routes)
  const layers = window.map.getStyle().layers;
  let beforeLayer: string | undefined;

  // Look for first symbol layer or any layer with higher z-index
  for (const layer of layers) {
    if (layer.type === 'symbol' || layer.id.includes('marker')) {
      beforeLayer = layer.id;
      break;
    }
  }
  console.log('[DRAG_PREVIEW] Will insert layer before:', beforeLayer);

  // Add preview line layer (dashed gray line)
  const layerExists = window.map.getLayer('drag-preview-lines');
  console.log('[DRAG_PREVIEW] drag-preview-lines layer exists:', !!layerExists);

  if (!layerExists) {
    console.log('[DRAG_PREVIEW] Creating drag-preview-lines layer');
    window.map.addLayer({
      id: 'drag-preview-lines',
      type: 'line',
      source: 'drag-preview-lines',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#808080',
        'line-width': 4,
        'line-dasharray': [2, 2],
        'line-opacity': 0.8
      }
    }, beforeLayer);
    console.log('[DRAG_PREVIEW] drag-preview-lines layer created');
  }
}

// Update preview layers with new data
function updatePreviewLayers(lineStrings: LineStringFeature[]) {
  console.log('[DRAG_PREVIEW] updatePreviewLayers called with', lineStrings.length, 'lineStrings');
  if (!window.map) {
    console.log('[DRAG_PREVIEW] window.map not available');
    return;
  }

  // Update preview lines
  const lineSource = window.map.getSource('drag-preview-lines') as mapboxgl.GeoJSONSource;
  console.log('[DRAG_PREVIEW] lineSource exists:', !!lineSource);

  if (lineSource) {
    const features = lineStrings.map(ls => ls.toGeoJson());
    console.log('[DRAG_PREVIEW] Updating source with', features.length, 'features');
    lineSource.setData({
      type: 'FeatureCollection',
      features
    });
    console.log('[DRAG_PREVIEW] Source updated successfully');
  } else {
    console.log('[DRAG_PREVIEW] lineSource not found, cannot update');
  }
}

// Clear preview layers
function clearPreviewLayers() {
  console.log('[DRAG_PREVIEW] clearPreviewLayers called');
  if (!window.map) {
    console.log('[DRAG_PREVIEW] window.map not available');
    return;
  }

  const lineSource = window.map.getSource('drag-preview-lines') as mapboxgl.GeoJSONSource;
  console.log('[DRAG_PREVIEW] lineSource exists:', !!lineSource);

  if (lineSource) {
    console.log('[DRAG_PREVIEW] Clearing preview data');
    lineSource.setData({
      type: 'FeatureCollection',
      features: []
    });
    console.log('[DRAG_PREVIEW] Preview data cleared');
  }
}

// Calculate preview routes with GraphHopper
async function calculatePreviewRoutes(
  context: ActionContextType,
  markerIndex: number,
  routeId: string,
  position: [number, number],
  isSnap: boolean
): Promise<void> {
  console.log('[DRAG_PREVIEW] calculatePreviewRoutes called', { markerIndex, routeId, position, isSnap });
  const state = context.state;
  const route = state.routes[routeId];
  if (!route) {
    console.log('[DRAG_PREVIEW] calculatePreviewRoutes - route not found');
    return;
  }

  // Cancel any pending request
  if (state.dragPreview.pendingRequest) {
    state.dragPreview.pendingRequest.abort();
  }

  // Create new abort controller
  const controller = new AbortController();
  context.commit('setDragPreviewPendingRequest', controller);

  try {
    const markers = route.markers;
    const previewLineStrings: LineStringFeature[] = [];
    let snappedPosition: [number, number] | null = null;

    console.log('[DRAG_PREVIEW] calculatePreviewRoutes - isSnap:', isSnap);
    // If snap is enabled, get snapped position first
    if (isSnap) {
      // For now, we'll use the position as-is
      // In a real implementation, we'd call a snap endpoint
      snappedPosition = position;
      console.log('[DRAG_PREVIEW] calculatePreviewRoutes - snappedPosition set to:', snappedPosition);
    } else {
      snappedPosition = position;
      console.log('[DRAG_PREVIEW] calculatePreviewRoutes - snappedPosition set to (no snap):', snappedPosition);
    }

    // Update snapped position in state
    context.commit('updateDragPreviewSnappedPosition', snappedPosition);

    // Calculate preview routes for affected segments
    if (markerIndex === 0 && markers.length > 1) {
      // First marker - update first segment
      const nextMarker = markers[1];

      // Create temporary marker for snapped position
      const tempStartMarker = new MarkerFeature(
        new MarkerGeometry(snappedPosition),
        new MarkerProperties('temp-preview-start', isSnap, Date.now().toString(), 0)
      );

      const lineString = await snapPoints(
        tempStartMarker,
        nextMarker,
        state.routePreference
      );
      previewLineStrings.push(lineString);
    } else if (markerIndex === markers.length - 1 && markers.length > 1) {
      // Last marker - update last segment
      const prevMarker = markers[markerIndex - 1];

      // Create temporary marker for snapped position
      const tempEndMarker = new MarkerFeature(
        new MarkerGeometry(snappedPosition),
        new MarkerProperties('temp-preview-end', isSnap, Date.now().toString(), 0)
      );

      const lineString = await snapPoints(
        prevMarker,
        tempEndMarker,
        state.routePreference
      );
      previewLineStrings.push(lineString);
    } else if (markerIndex > 0 && markerIndex < markers.length - 1) {
      // Middle marker - update both adjacent segments
      const prevMarker = markers[markerIndex - 1];
      const nextMarker = markers[markerIndex + 1];

      // Create temporary marker for snapped position
      const tempMidMarker = new MarkerFeature(
        new MarkerGeometry(snappedPosition),
        new MarkerProperties('temp-preview-mid', isSnap, Date.now().toString(), 0)
      );

      // Previous segment
      const prevLineString = await snapPoints(
        prevMarker,
        tempMidMarker,
        state.routePreference
      );
      previewLineStrings.push(prevLineString);

      // Next segment
      const nextLineString = await snapPoints(
        tempMidMarker,
        nextMarker,
        state.routePreference
      );
      previewLineStrings.push(nextLineString);
    }

    // Check if we're still dragging before updating
    // (in case drag ended while we were calculating)
    if (!context.state.dragPreview.isDragging) {
      console.log('[DRAG_PREVIEW] calculatePreviewRoutes - drag ended, skipping update');
      return;
    }

    // Update preview in state
    context.commit('updateDragPreviewLineStrings', previewLineStrings);

    // Update map layers only if still dragging
    if (context.state.dragPreview.isDragging) {
      updatePreviewLayers(previewLineStrings);
    }

  } catch (error: any) {
    if (error.name !== 'AbortError') {
      console.error('Error calculating preview routes:', error);
    }
  }
}

// Debounced version of calculatePreviewRoutes
const debouncedCalculatePreview = debounce(calculatePreviewRoutes, DRAG_PREVIEW_DEBOUNCE_MS);

// Cancel any pending preview calculations
function cancelPendingCalculations(state: PlanTrailState) {
  console.log('[DRAG_PREVIEW] cancelPendingCalculations called');

  // Cancel debounced calls
  debouncedCalculatePreview.cancel();

  // Abort any in-flight requests
  if (state.dragPreview.pendingRequest) {
    state.dragPreview.pendingRequest.abort();
  }
}

export const dragPreviewActions = {
  // Initialize drag preview
  initializeDragPreview({ commit }: ActionContextType, payload: {
    markerIndex: number;
    routeId: string;
    position: [number, number]
  }) {
    console.log('[DRAG_PREVIEW] initializeDragPreview called', payload);
    // Create preview layers if they don't exist
    createPreviewLayers();

    // Start drag preview state
    commit('startDragPreview', payload);
    console.log('[DRAG_PREVIEW] Drag preview initialized');
  },

  // Update drag preview during drag
  async updateDragPreview(
    context: ActionContextType,
    position: [number, number]
  ) {
    console.log('[DRAG_PREVIEW] updateDragPreview called', position);
    const { dragPreview, isSnap } = context.state;

    if (!dragPreview.isDragging ||
        dragPreview.markerIndex === null ||
        !dragPreview.routeId) {
      console.log('[DRAG_PREVIEW] updateDragPreview - not in dragging state');
      return;
    }

    console.log('[DRAG_PREVIEW] updateDragPreview - updating position');
    // Update preview position
    context.commit('updateDragPreviewPosition', position);

    // Calculate preview routes (debounced)
    console.log('[DRAG_PREVIEW] updateDragPreview - calling debouncedCalculatePreview');
    await debouncedCalculatePreview(
      context,
      dragPreview.markerIndex,
      dragPreview.routeId,
      position,
      isSnap
    );
  },

  // Finalize drag preview
  async finalizeDragPreview({ state, commit, dispatch }: ActionContextType) {
    const { dragPreview } = state;

    // Cancel any pending calculations first
    cancelPendingCalculations(state);

    // Always clear preview layers and end drag preview state
    const cleanup = () => {
      clearPreviewLayers();
      commit('endDragPreview');
    };

    if (!dragPreview.isDragging ||
        dragPreview.markerIndex === null ||
        !dragPreview.routeId) {
      cleanup();
      return false; // Return false to indicate preview wasn't applied
    }

    // If we don't have a snapped position yet (drag was too quick), return false
    if (!dragPreview.snappedPosition) {
      cleanup();
      return false; // Return false to indicate preview wasn't applied
    }

    // Apply the snapped position to the actual marker
    const route = state.routes[dragPreview.routeId];
    if (route && route.markers[dragPreview.markerIndex]) {
      const marker = route.markers[dragPreview.markerIndex];

      // Capture old position for the undo command
      const oldPosition = [...marker.markerGeometry.coordinates];

      // Update marker position to snapped position
      marker.markerGeometry.coordinates = [...dragPreview.snappedPosition];

      // Call markerMoved with oldPosition so it creates a MoveMarkerCommand
      await dispatch('markerMoved', { marker, oldPosition });
    } else {
      cleanup();
      return false;
    }

    // Clear preview
    cleanup();
    return true; // Return true to indicate preview was applied
  },

  // Cancel drag preview
  cancelDragPreview({ state, commit }: ActionContextType) {
    console.log('[DRAG_PREVIEW] cancelDragPreview called');
    // Cancel any pending calculations
    cancelPendingCalculations(state);
    clearPreviewLayers();
    commit('endDragPreview');
  }
};
